import { Card, CardBody, CardHeader, Heading, Text } from '@chakra-ui/react'
import { DDSTheme, ddsTokens } from '@ds/index'
import { useMemo } from 'react'
import {
	Area,
	CartesianGrid,
	ComposedChart,
	Legend,
	Line,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts'

import { DateRange, Granularity, MeetingBookedData, TimeFrame } from '../types'
import { getTimeRanges } from '../utils'
import ChartSpinner from './ChartSpinner'
import ChartTooltip from './ChartTooltip'
import LegendContent from './LegendContent'

interface StackedChartDataPoint {
	timeLabel: string
	booking_rate_21d: number | null
	meetings_table_booking_rate_21d: number | null
}

interface MeetingBookedRateProps {
	rawData: MeetingBookedData[] | undefined
	granularity: Granularity
	datePreset: TimeFrame
	customDateRange?: DateRange
	isLoading?: boolean
}

const MeetingBookedRate = ({
	rawData,
	granularity,
	datePreset,
	customDateRange,
	isLoading = false,
}: MeetingBookedRateProps) => {
	const tokens = ddsTokens[DDSTheme.light]
	const processedData = useMemo((): StackedChartDataPoint[] => {
		if (!rawData || rawData.length === 0) {
			return []
		}
		const timeRanges = getTimeRanges(granularity, datePreset)
		const dataForChart = timeRanges.map((range) => {
			const periodEntries = rawData.filter((entry) => {
				if (!entry.submission_date) return false
				const entryDate = new Date(entry.submission_date)
				return entryDate >= range.start && entryDate <= range.end
			})
			let sumBookingRate = 0
			let countBookingRate = 0
			let sumMeetingTableRate = 0
			let countMeetingTableRate = 0
			periodEntries.forEach((entry) => {
				if (entry.booking_rate_21d !== null) {
					const rate = parseFloat(entry.booking_rate_21d)
					if (!isNaN(rate)) {
						sumBookingRate += rate * 100
						countBookingRate++
					}
				}
				if (entry.meeting_table_booking_rate_21d !== null) {
					const rate = parseFloat(entry.meeting_table_booking_rate_21d)
					if (!isNaN(rate)) {
						sumMeetingTableRate += rate * 100
						countMeetingTableRate++
					}
				}
			})
			return {
				timeLabel: range.label,
				booking_rate_21d:
					countBookingRate > 0 ? sumBookingRate / countBookingRate : 0,
				meetings_table_booking_rate_21d:
					countMeetingTableRate > 0
						? sumMeetingTableRate / countMeetingTableRate
						: 0,
			}
		})
		return dataForChart
	}, [rawData, granularity, datePreset, customDateRange])

	const areaColor1 = tokens.primitive.light.success[600]
	const lineColor1 = tokens.primitive.light.success[600]

	const areaColor2 = tokens.primitive.light.primary[400]

	return (
		<Card
			shadow="sm"
			p={4}
			border={`1px solid ${tokens.primitive.light.gray[100]}`}
		>
			<CardHeader p={0} mb={2}>
				<Heading size="sm">Meeting Booked Rate</Heading>
				<Text fontSize="sm" color="gray.500">
					Percentage of scheduling opportunities with a meeting booked outcome
				</Text>
			</CardHeader>
			<CardBody p={0} position="relative" flexGrow={1}>
				{isLoading && <ChartSpinner />}
				<ResponsiveContainer width="100%" height={250}>
					<ComposedChart
						data={processedData}
						margin={{ top: 24, right: 20, bottom: 0 }}
					>
						<CartesianGrid horizontal vertical={false} stroke="#F1F1F1" />
						<defs>
							<linearGradient id="inboundGradient" x1="0" y1="0" x2="0" y2="1">
								<stop offset="5%" stopColor={areaColor1} stopOpacity={0.15} />
								<stop offset="95%" stopColor={areaColor1} stopOpacity={0.01} />
							</linearGradient>
							<linearGradient id="overallGradient" x1="0" y1="0" x2="0" y2="1">
								<stop offset="5%" stopColor={areaColor2} stopOpacity={0.3} />{' '}
								{/* Darker opacity for purple */}
								<stop offset="95%" stopColor={areaColor2} stopOpacity={0.05} />
							</linearGradient>
						</defs>
						<Legend content={<LegendContent />} />
						<XAxis
							dataKey="timeLabel"
							tickLine={false}
							tick={{ fill: '#666666' }}
							dy={5}
						/>
						<YAxis
							tickLine={false}
							stroke="transparent"
							tick={{ fill: '#666666' }}
						/>
						<Tooltip
							contentStyle={{
								backgroundColor: tokens.primitive.light.white[100],
								borderColor: tokens.primitive.light.gray[200],
								color: tokens.primitive.light.gray[800],
							}}
							content={(props) => (
								<ChartTooltip
									{...props}
									formatValue={(value) => `${(value as number).toFixed(1)}%`}
								/>
							)}
						/>
						<Area
							type="monotone"
							dataKey="booking_rate_21d"
							fill="url(#inboundGradient)"
							fillOpacity={1}
							tooltipType="none"
							connectNulls
							legendType="none"
						/>
						<Line
							dataKey="booking_rate_21d"
							dot={true}
							stroke={lineColor1}
							strokeWidth={2}
							type="monotone"
							name="Inbound"
							connectNulls
						/>
					</ComposedChart>
				</ResponsiveContainer>
			</CardBody>
		</Card>
	)
}

export default MeetingBookedRate
